import { makeAutoObservable } from 'mobx';
import axiosBarwiseInstance from 'libs/axios/axios.lib';
import { toast } from 'react-toastify';

class ThemeStore {
  defaultThemeConfig = {};
  themeConfig = {};
  isLoading = false;
  currentTemplateId = null;

  constructor() {
    makeAutoObservable(this);
  }

  getIsLoading() {
    return this.isLoading;
  }

  setIsLoading(isLoading) {
    this.isLoading = isLoading;
    return this.isLoading;
  }

  getCurrentTemplateId() {
    return this.currentTemplateId;
  }

  setCurrentTemplateId(currentTemplateId) {
    this.currentTemplateId = currentTemplateId;
    return this.currentTemplateId;
  }
  getColors() {
    return this.themeConfig.colors;
  }

  getThemeConfig() {
    return this.themeConfig;
  }

  getDefaultThemeConfig() {
    return this.defaultThemeConfig;
  }

  // Get current dark logo URL
  getDarkLogoUrl() {
    return this.themeConfig.darkLogoUrl;
  }

  // Get current light logo URL
  getLightLogoUrl() {
    return this.themeConfig.lightLogoUrl;
  }

  // Get current login image URL
  getLoginImageUrl() {
    return this.themeConfig.loginImageUrl;
  }

  // Update entire theme configuration
  setThemeConfig(themeConfig) {
    this.themeConfig = { ...themeConfig };
    return this.themeConfig;
  }

  setDefaultThemeConfig(defaultThemeConfig) {
    this.defaultThemeConfig = { ...defaultThemeConfig };
    return this.defaultThemeConfig;
  }

  // Update colors only
  setColors(colors) {
    this.themeConfig = {
      ...this.themeConfig,
      colors: { ...colors }
    };
    return this.themeConfig;
  }

  // Update dark logo URL only
  setDarkLogoUrl(darkLogoUrl) {
    this.themeConfig = {
      ...this.themeConfig,
      darkLogoUrl
    };
    return this.themeConfig;
  }

  // Update light logo URL only
  setLightLogoUrl(lightLogoUrl) {
    this.themeConfig = {
      ...this.themeConfig,
      lightLogoUrl
    };
    return this.themeConfig;
  }

  // Update login image URL only
  setLoginImageUrl(loginImageUrl) {
    this.themeConfig = {
      ...this.themeConfig,
      loginImageUrl
    };
    return this.themeConfig;
  }

  // Update a specific color
  setColor(colorKey, value) {
    this.themeConfig = {
      ...this.themeConfig,
      colors: {
        ...this.themeConfig.colors,
        [colorKey]: value
      }
    };
    return this.themeConfig;
  }

  // Apply preset theme (colors only)
  applyPreset(presetColors) {
    this.themeConfig = {
      ...this.themeConfig,
      colors: { ...presetColors }
    };
    return this.themeConfig;
  }

  async getSetCurrentTheme() {
    this.setIsLoading(true);
    try {
      const response = await axiosBarwiseInstance.get('/api/current-theme');
      if (response.status === 200) {
        const config = response.data;
        this.setCurrentTemplateId(config.id);
        const themeConfig = {
          colors: {
            PRIMARY_MAIN: config.colors_primary,
            SECONDARY_MAIN: config.colors_secondary,
            BACKGROUND_MAIN: config.colors_background,
            BACKGROUND_SECONDARY: config.colors_surface
          },
          darkLogoUrl: config.logo_dark,
          lightLogoUrl: config.logo_light,
          loginImageUrl: config.login_image,
          logoIconUrl: config.logo_icon
        };
        this.setThemeConfig(themeConfig);
        this.setDefaultThemeConfig(themeConfig);
      }
    } catch (error) {
      toast.error('Error getting current theme');
    } finally {
      this.setIsLoading(false);
    }
  }

  async updateCurrentTheme(templateId) {
    const themeConfig = {
      templateId: templateId
    };
    try {
      this.setIsLoading(true);
      const response = await axiosBarwiseInstance.post('/api/options/update', {
        name: 'THEME_CONFIG',
        config: themeConfig
      });
      if (response.status === 200) {
        await this.getSetCurrentTheme();
        toast.success('Theme applied successfully');
      } else {
        throw new Error('Error updating current theme');
      }
    } catch (error) {
      toast.error('Error updating current theme');
    } finally {
      this.setIsLoading(false);
    }
  }

  // Reset to default configuration
  resetToDefault() {
    this.setThemeConfig(this.defaultThemeConfig);
    return this.defaultThemeConfig;
  }
}

const themeStore = new ThemeStore();
export default themeStore;
